import { useSelector } from 'react-redux';
import {
    artikelGruppenByNrSelector,
    betreuerByNrSelector,
    branchenByNrSelector,
    laenderByIkz2Selector,
    produktgruppenByNrSelector,
    produzentenByNrSelector,
} from 'store/lookups/selector';
import * as React from 'react';
import { uniq } from 'lodash';
import { useKundeBrancheList, useKundeList } from 'api/kunde';
import { createKundeUrl } from 'utils/formatter';
import {
    FilterValues,
    UmsatzSummary,
    defaultLineData,
    LineData,
    UmsatzSummaryPosition,
    UmsatzSummaryRecord,
    UmsatzSummaryGroupBy,
} from '../types';
import { useUmsatzSummaryData } from './useUmsatzSummaryData';

export interface UmsatzSummaryProps extends FilterValues {
    enabled: boolean;
}

const groupByRecordList: UmsatzSummaryGroupBy[] = ['kunde', 'branche', 'betreuer', 'telBetreuer', 'land'];

function getBundeslandKzFromPostleitzahl(plz: string): string {
    // eslint-disable-next-line max-len
    // https://de.wikipedia.org/wiki/Postleitzahl_(Österreich)#/media/Datei:Briefzentren_der_Österreichischen_Post_AG.png
    // https://de.wikipedia.org/wiki/Land_(Österreich)#/media/Datei:Austria,_administrative_divisions_-_de-.svg

    if (plz.startsWith('1')) {
        return 'WI';
    }
    if (plz.startsWith('2')) {
        if (plz.startsWith('24')) {
            return 'BL';
        }
        return 'NO';
    }
    if (plz.startsWith('3')) {
        return 'NO';
    }
    if (plz.startsWith('4')) {
        return 'OO';
    }
    if (plz.startsWith('5')) {
        if (plz.startsWith('51') || plz.startsWith('52')) {
            return 'OO';
        }
        return 'SB';
    }
    if (plz.startsWith('6')) {
        if (plz.startsWith('67') || plz.startsWith('68') || plz.startsWith('69')) {
            return 'VB';
        }
        return 'TI';
    }
    if (plz.startsWith('7')) {
        return 'BL';
    }
    if (plz.startsWith('8')) {
        return 'ST';
    }
    if (plz.startsWith('9')) {
        if (plz.startsWith('99')) {
            return 'TI';
        }
        return 'KA';
    }

    return '?';
}

const bundeslaender: { [key: string]: string } = {
    WI: 'Wien',
    NO: 'Niederösterreich',
    BL: 'Burgenland',
    OO: 'Oberösterreich',
    ST: 'Steiermark',
    KA: 'Kärnten',
    SB: 'Salzburg',
    TI: 'Tirol',
    VB: 'Vorarlberg',
};

export const useUmsatzSummary = (props: UmsatzSummaryProps): UmsatzSummary => {
    const { groupBy } = props;

    const betreuerByNr = useSelector(betreuerByNrSelector);
    const produktgruppenByNr = useSelector(produktgruppenByNrSelector);
    const branchenByNr = useSelector(branchenByNrSelector);
    const produzentenByNr = useSelector(produzentenByNrSelector);
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const laenderByIkz2 = useSelector(laenderByIkz2Selector);

    const umsatzSummaryData = useUmsatzSummaryData(props);

    const kundeNrList = React.useMemo(() => {
        return umsatzSummaryData.recordList.map((r) => r.kNr);
    }, [umsatzSummaryData.recordList]);

    const kundeBrancheList = useKundeBrancheList(kundeNrList, { enabled: groupBy === 'branche' });

    const hauptlieferantNrList = React.useMemo<number[]>(() => {
        return uniq(umsatzSummaryData.recordPositionList.map((r) => r.hauptlieferant?.lNr || 0).filter((lNr) => !!lNr));
    }, [umsatzSummaryData.recordPositionList]);

    const lieferantList = useKundeList(
        { nrList: hauptlieferantNrList, includeArchiv: true },
        {
            enabled: groupBy === 'lieferant',
        },
    );

    const addValuesToGroupedLineData = (
        groupedLineData: LineData,
        recordNr: string,
        lineData: LineData | undefined,
        total: LineData,
    ) => {
        /* eslint-disable no-param-reassign */

        const recordNrList = uniq([...groupedLineData.recordNrList, recordNr]);

        groupedLineData.count = recordNrList.length;
        groupedLineData.countP = groupedLineData.count / (total.count / 100);
        groupedLineData.recordNrList = recordNrList;
        groupedLineData.ekNetto += lineData?.ekNetto || 0;
        groupedLineData.ekNettoP = groupedLineData.ekNetto / (total.ekNetto / 100);
        groupedLineData.vkNetto += lineData?.vkNetto || 0;
        groupedLineData.vkNettoP = groupedLineData.vkNetto / (total.vkNetto / 100);
        groupedLineData.bMarge += lineData?.bMarge || 0;
        groupedLineData.bMargeP = groupedLineData.bMarge / (total.bMarge / 100);
        groupedLineData.nMarge += lineData?.nMarge || 0;
        groupedLineData.nMargeP = groupedLineData.nMarge / (total.nMarge / 100);
        groupedLineData.bAnteil += lineData?.bAnteil || 0;
        groupedLineData.fAnteil += lineData?.fAnteil || 0;
    };

    const getGroupById = React.useCallback(
        (record: UmsatzSummaryRecord, position?: UmsatzSummaryPosition): string => {
            switch (groupBy) {
                case 'kunde':
                    return record.kNr.toString();
                case 'produktgruppe':
                    return (position?.artikelDetail?.produktGruppeNr || 0).toString();
                case 'artikelGruppe':
                    return (position?.artikelGruppeNr || 0).toString();
                case 'produzent':
                    return (position?.artikelDetail?.produzentNr || 0).toString();
                case 'branche':
                    return ((kundeBrancheList.data || []).find((i) => i.kNr === record.kNr)?.bNr || 0).toString();
                case 'lieferant':
                    return (position?.hauptlieferant?.lNr || 0).toString();
                case 'betreuer':
                    return record.betreuer.toString();
                case 'telBetreuer':
                    return record.telBetreuer.toString();
                case 'land':
                    if (record.kunde?.land === 'AT') {
                        return record.kunde.land + '|' + getBundeslandKzFromPostleitzahl(record.kunde.plz);
                    }
                    return record.kunde?.land || '?';

                default:
                    return '?';
            }
        },
        [groupBy, kundeBrancheList.data],
    );

    const getGroupByData = React.useCallback(
        (
            groupById: string,
            record: UmsatzSummaryRecord,
        ): { groupByLabel: string; groupByNote?: string; groupByLink?: string } => {
            switch (groupBy) {
                case 'kunde':
                    return {
                        groupByLabel: (record.kunde?.name1 || '?') + (record.kunde?.archiv ? ' (archiviert)' : ''),
                        groupByNote:
                            'Nr: ' +
                            record.kNr.toString() +
                            ', Betr: ' +
                            (record.betreuer ? betreuerByNr[record.betreuer]?.name || record.betreuer : '') +
                            ' / ' +
                            (record.telBetreuer ? betreuerByNr[record.telBetreuer]?.name || record.telBetreuer : ''),
                        groupByLink: record.kunde?.nr ? createKundeUrl(record.kunde.nr.toString()) : undefined,
                    };
                case 'produktgruppe':
                    return {
                        groupByLabel: produktgruppenByNr[groupById]?.text || groupById,
                    };
                case 'artikelGruppe':
                    return {
                        groupByLabel: artikelGruppenByNr[groupById]?.text || groupById,
                    };
                case 'produzent':
                    return {
                        groupByLabel: produzentenByNr[groupById]?.name || groupById,
                    };
                case 'branche':
                    return {
                        groupByLabel: branchenByNr[groupById]?.text || groupById,
                    };
                case 'lieferant':
                    return {
                        groupByLabel:
                            (lieferantList.data || []).find((l) => l.nr.toString() === groupById)?.name1 || groupById,
                    };
                case 'betreuer':
                    return {
                        groupByLabel: betreuerByNr[groupById]?.name || groupById,
                    };
                case 'telBetreuer':
                    return { groupByLabel: betreuerByNr[groupById]?.name || groupById };
                case 'land': {
                    const [landKz, bundeslandKz] = groupById.split('|');
                    if (bundeslandKz) {
                        return {
                            groupByLabel:
                                (laenderByIkz2[landKz].text || '?') +
                                ' - ' +
                                (bundeslaender[bundeslandKz] || bundeslandKz),
                        };
                    }
                    return {
                        groupByLabel: laenderByIkz2[landKz].text || '?',
                    };
                }
                default:
                    return {
                        groupByLabel: '?',
                    };
            }
        },
        [
            groupBy,
            produktgruppenByNr,
            produzentenByNr,
            branchenByNr,
            betreuerByNr,
            lieferantList.data,
            artikelGruppenByNr,
            laenderByIkz2,
        ],
    );

    return React.useMemo<UmsatzSummary>(() => {
        const groupedLineData: { [key: string]: LineData } = {};
        const totalLineData: LineData = { ...defaultLineData };

        umsatzSummaryData.recordList.forEach((record) => {
            const filteredRecordPositionList = umsatzSummaryData.recordPositionList.filter(
                (position) => position.recordNr === record.nr && position.lArt !== 'X',
            );

            let groupById: string;
            if (groupByRecordList.includes(groupBy)) {
                groupById = getGroupById(record);
            }

            filteredRecordPositionList.forEach((position) => {
                if (!groupByRecordList.includes(groupBy)) {
                    groupById = getGroupById(record, position);
                }

                if (!groupedLineData[groupById]) {
                    groupedLineData[groupById] = {
                        ...defaultLineData,
                        groupById,
                        ...getGroupByData(groupById, record),
                    };
                }

                addValuesToGroupedLineData(
                    groupedLineData[groupById],
                    position.recordNr,
                    position.lineData,
                    umsatzSummaryData.total,
                );
            });
        });

        Object.values(groupedLineData).forEach((lineData) => {
            totalLineData.count += lineData.count;
            totalLineData.countP = 100;
            totalLineData.ekNetto += lineData.ekNetto;
            totalLineData.ekNettoP = 100;
            totalLineData.vkNetto += lineData.vkNetto;
            totalLineData.vkNettoP = 100;
            totalLineData.bMarge += lineData.bMarge;
            totalLineData.bMargeP = 100;
            totalLineData.nMarge += lineData.nMarge;
            totalLineData.nMargeP = 100;
            totalLineData.bAnteil += lineData.bAnteil;
            totalLineData.fAnteil += lineData.fAnteil;
        });

        return {
            loading: umsatzSummaryData.isLoading,
            count: 0,
            total: totalLineData,
            recordList: umsatzSummaryData.recordList,
            recordPositionList: umsatzSummaryData.recordPositionList,
            lineDataList: Object.values(groupedLineData),
            fAnteilTotal: 0,
        };
    }, [
        groupBy,
        getGroupById,
        getGroupByData,
        umsatzSummaryData.isLoading,
        umsatzSummaryData.total,
        umsatzSummaryData.recordList,
        umsatzSummaryData.recordPositionList,
    ]);
};
